import { createSlice } from "@reduxjs/toolkit";
import {timeSince} from "./utils";

const initialState = {
    workspaceId: null,
    workspaceMembership: null,
    workspaceMemberships: [],
    userRoles: [],
    tasks: [],
    projects: [],
    goals: [],
    teams: [],
    customers: [],
    subscriptionPlans: [],
    orders: [],
    invoices: [],
    payments: [],
    subscriptions: [],
    billingProfiles: [],
    creditWallets: [],
    creditLedgerEntries: [],
    manualPaymentRequests: [],
    chats: [],
    chatMessages: [],
    chatParticipants: [],
    inboxItems: [],
    users: [],
    lastEvent: null,
};

function normalizeTask(item) {
    if (!item || typeof item !== "object") return item;

    const dueDateUnix = item.dueDate
        ? new Date(item.dueDate).getTime() / 1000
        : null;
    const timeRemain = item.dueDate
        ? timeSince(dueDateUnix, "YYYY/MM/DD", {thisWeekThreshold: true})
        : null;

    return item.timeRemain === timeRemain && item.dueDateUnix === dueDateUnix
        ? item
        : {...item, timeRemain, dueDateUnix};
}

function upsertByDocumentId(items, item, normalizeItem = (value) => value) {
    if (!item) return items;
    const normalizedItem = normalizeItem(item);
    const key = normalizedItem.documentId ?? normalizedItem.id;
    const index = items.findIndex((current) => (current.documentId ?? current.id) === key);
    if (index >= 0) {
        const next = [...items];
        next[index] = {...next[index], ...normalizedItem};
        return next;
    }
    return [...items, normalizedItem];
}

function normalizeByDocumentId(items, normalizeItem = (value) => value) {
    if (!Array.isArray(items)) return [];
    return items.reduce((acc, item) => upsertByDocumentId(acc, item, normalizeItem), []);
}

function mergeByDocumentId(items, incomingItems, normalizeItem = (value) => value) {
    if (!Array.isArray(incomingItems) || !incomingItems.length) return Array.isArray(items) ? items : [];

    const currentItems = Array.isArray(items) ? items : [];
    const currentByKey = new Map(
        currentItems.map((item) => [item.documentId ?? item.id, item])
    );
    const seenKeys = new Set();
    const next = [];

    incomingItems.forEach((item) => {
        if (!item) return;
        const normalizedItem = normalizeItem(item);
        const key = normalizedItem.documentId ?? normalizedItem.id;
        seenKeys.add(key);
        const current = currentByKey.get(key);
        next.push(current ? {...current, ...normalizedItem} : normalizedItem);
    });

    currentItems.forEach((item) => {
        const key = item.documentId ?? item.id;
        if (!seenKeys.has(key)) {
            next.push(item);
        }
    });

    return next;
}

function removeByDocumentId(items, item) {
    const key = item?.documentId ?? item?.id;
    return items.filter((current) => (current.documentId ?? current.id) !== key);
}

function sameEntity(left, right) {
    const leftKeys = [left?.documentId, left?.id]
        .filter((value) => value !== null && value !== undefined)
        .map(String);
    return [right?.documentId, right?.id]
        .filter((value) => value !== null && value !== undefined)
        .map(String)
        .some((key) => leftKeys.includes(key));
}

function patchMembershipUserRole(membership, role, remove = false) {
    if (!membership || !role) return membership;
    const roleMembership = role.workspace_membership;
    const belongsToMembership = sameEntity(roleMembership, membership)
        || sameEntity(roleMembership?.member, membership?.member);
    if (!belongsToMembership) return membership;

    return {
        ...membership,
        user_roles: remove
            ? removeByDocumentId(membership.user_roles || [], role)
            : upsertByDocumentId(membership.user_roles || [], role),
    };
}

function collectWorkspaceTasks(workspace) {
    const tasks = [
        ...(Array.isArray(workspace?.tasks) ? workspace.tasks : []),
        ...(Array.isArray(workspace?.projects)
            ? workspace.projects.flatMap((project) => Array.isArray(project?.tasks) ? project.tasks : [])
            : []),
    ];

    return normalizeByDocumentId(tasks, normalizeTask);
}

const workspaceGraphSlice = createSlice({
    name: "workspaceGraph",
    initialState,
    reducers: {
        hydrateWorkspaceSnapshot(state, action) {
            const payload = action.payload || {};
            state.workspaceId = payload.workspaceId ?? state.workspaceId;
            state.workspaceMembership = payload.workspaceMembership ?? state.workspaceMembership;
            state.workspaceMemberships = Array.isArray(payload.workspaceMemberships)
                ? normalizeByDocumentId(payload.workspaceMemberships)
                : state.workspaceMemberships;
            state.userRoles = Array.isArray(payload.userRoles)
                ? normalizeByDocumentId(payload.userRoles)
                : payload.workspaceMembership
                    ? normalizeByDocumentId(payload.workspaceMembership.user_roles || [])
                    : state.userRoles;
            state.tasks = Array.isArray(payload.tasks)
                ? normalizeByDocumentId(payload.tasks, normalizeTask)
                : state.tasks;
            state.projects = Array.isArray(payload.projects)
                ? normalizeByDocumentId(payload.projects)
                : state.projects;
            state.goals = Array.isArray(payload.goals)
                ? normalizeByDocumentId(payload.goals)
                : state.goals;
            state.teams = Array.isArray(payload.teams)
                ? normalizeByDocumentId(payload.teams)
                : state.teams;
        state.customers = Array.isArray(payload.customers)
            ? normalizeByDocumentId(payload.customers)
            : state.customers;
        state.subscriptionPlans = Array.isArray(payload.subscriptionPlans)
            ? normalizeByDocumentId(payload.subscriptionPlans)
            : state.subscriptionPlans;
        state.orders = Array.isArray(payload.orders)
            ? normalizeByDocumentId(payload.orders)
            : state.orders;
        state.invoices = Array.isArray(payload.invoices)
            ? normalizeByDocumentId(payload.invoices)
            : state.invoices;
        state.payments = Array.isArray(payload.payments)
            ? normalizeByDocumentId(payload.payments)
            : state.payments;
        state.subscriptions = Array.isArray(payload.subscriptions)
            ? normalizeByDocumentId(payload.subscriptions)
            : state.subscriptions;
        state.billingProfiles = Array.isArray(payload.billingProfiles)
            ? normalizeByDocumentId(payload.billingProfiles)
            : state.billingProfiles;
        state.creditWallets = Array.isArray(payload.creditWallets)
            ? normalizeByDocumentId(payload.creditWallets)
            : state.creditWallets;
        state.creditLedgerEntries = Array.isArray(payload.creditLedgerEntries)
            ? normalizeByDocumentId(payload.creditLedgerEntries)
            : state.creditLedgerEntries;
        state.manualPaymentRequests = Array.isArray(payload.manualPaymentRequests)
            ? normalizeByDocumentId(payload.manualPaymentRequests)
            : state.manualPaymentRequests;
        state.chats = Array.isArray(payload.chats)
            ? normalizeByDocumentId(payload.chats)
            : state.chats;
        state.chatMessages = Array.isArray(payload.chatMessages)
            ? normalizeByDocumentId(payload.chatMessages)
            : state.chatMessages;
        state.chatParticipants = Array.isArray(payload.chatParticipants)
            ? normalizeByDocumentId(payload.chatParticipants)
            : state.chatParticipants;
        state.inboxItems = Array.isArray(payload.inboxItems)
            ? normalizeByDocumentId(payload.inboxItems)
            : state.inboxItems;
        state.users = payload.users ?? state.users;
        },
        setWorkspaceGraphWorkspaceId(state, action) {
            state.workspaceId = action.payload;
        },
        setWorkspaceGraphMembership(state, action) {
            state.workspaceMembership = action.payload;
            state.userRoles = normalizeByDocumentId(action.payload?.user_roles || []);
        },
        setWorkspaceGraphMemberships(state, action) {
            state.workspaceMemberships = normalizeByDocumentId(action.payload || []);
        },
        resetWorkspaceGraph(state, action) {
            return {
                ...initialState,
                workspaceMemberships: normalizeByDocumentId(action.payload || []),
            };
        },
        setWorkspaceGraphTasks(state, action) {
            state.tasks = normalizeByDocumentId(action.payload || [], normalizeTask);
        },
        mergeWorkspaceTasks(state, action) {
            state.tasks = mergeByDocumentId(
                state.tasks,
                Array.isArray(action.payload) ? action.payload : action.payload ? [action.payload] : [],
                normalizeTask
            );
        },
        setWorkspaceGraphProjects(state, action) {
            state.projects = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphGoals(state, action) {
            state.goals = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphTeams(state, action) {
            state.teams = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCustomers(state, action) {
            state.customers = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphSubscriptionPlans(state, action) {
            state.subscriptionPlans = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphOrders(state, action) {
            state.orders = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphInvoices(state, action) {
            state.invoices = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphPayments(state, action) {
            state.payments = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphSubscriptions(state, action) {
            state.subscriptions = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphBillingProfiles(state, action) {
            state.billingProfiles = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCreditWallets(state, action) {
            state.creditWallets = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCreditLedgerEntries(state, action) {
            state.creditLedgerEntries = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphManualPaymentRequests(state, action) {
            state.manualPaymentRequests = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphChats(state, action) {
            state.chats = normalizeByDocumentId(action.payload || []);
        },
        mergeWorkspaceChats(state, action) {
            state.chats = mergeByDocumentId(state.chats, Array.isArray(action.payload) ? action.payload : [action.payload].filter(Boolean));
        },
        setWorkspaceGraphChatMessages(state, action) {
            state.chatMessages = normalizeByDocumentId(action.payload || []);
        },
        mergeWorkspaceChatMessages(state, action) {
            state.chatMessages = mergeByDocumentId(state.chatMessages, Array.isArray(action.payload) ? action.payload : [action.payload].filter(Boolean));
        },
        replaceWorkspaceChatMessagesForChat(state, action) {
            const chat = action.payload?.chat;
            const items = Array.isArray(action.payload?.items) ? action.payload.items : [];
            const otherChatMessages = state.chatMessages.filter((item) => !sameEntity(item?.chat, chat));
            state.chatMessages = normalizeByDocumentId([...otherChatMessages, ...items]);
        },
        setWorkspaceGraphChatParticipants(state, action) {
            state.chatParticipants = normalizeByDocumentId(action.payload || []);
        },
        mergeWorkspaceChatParticipants(state, action) {
            state.chatParticipants = mergeByDocumentId(state.chatParticipants, Array.isArray(action.payload) ? action.payload : [action.payload].filter(Boolean));
        },
        setWorkspaceGraphInboxItems(state, action) {
            state.inboxItems = normalizeByDocumentId(action.payload || []);
        },
        mergeWorkspaceInboxItems(state, action) {
            state.inboxItems = mergeByDocumentId(state.inboxItems, Array.isArray(action.payload) ? action.payload : [action.payload].filter(Boolean));
        },
        upsertWorkspaceTask(state, action) {
            state.tasks = upsertByDocumentId(state.tasks, action.payload, normalizeTask);
        },
        upsertWorkspaceProject(state, action) {
            state.projects = upsertByDocumentId(state.projects, action.payload);
        },
        upsertWorkspaceGoal(state, action) {
            state.goals = upsertByDocumentId(state.goals, action.payload);
        },
        upsertWorkspaceTeam(state, action) {
            state.teams = upsertByDocumentId(state.teams, action.payload);
        },
        upsertWorkspaceCustomer(state, action) {
            state.customers = upsertByDocumentId(state.customers, action.payload);
        },
        upsertWorkspaceOrder(state, action) {
            state.orders = upsertByDocumentId(state.orders, action.payload);
        },
        upsertWorkspaceInvoice(state, action) {
            state.invoices = upsertByDocumentId(state.invoices, action.payload);
        },
        upsertWorkspacePayment(state, action) {
            state.payments = upsertByDocumentId(state.payments, action.payload);
        },
        upsertWorkspaceSubscription(state, action) {
            state.subscriptions = upsertByDocumentId(state.subscriptions, action.payload);
        },
        upsertWorkspaceBillingProfile(state, action) {
            state.billingProfiles = upsertByDocumentId(state.billingProfiles, action.payload);
        },
        upsertWorkspaceCreditWallet(state, action) {
            state.creditWallets = upsertByDocumentId(state.creditWallets, action.payload);
        },
        upsertWorkspaceCreditLedgerEntry(state, action) {
            state.creditLedgerEntries = upsertByDocumentId(state.creditLedgerEntries, action.payload);
        },
        upsertWorkspaceManualPaymentRequest(state, action) {
            state.manualPaymentRequests = upsertByDocumentId(state.manualPaymentRequests, action.payload);
        },
        upsertWorkspaceChat(state, action) {
            state.chats = upsertByDocumentId(state.chats, action.payload);
        },
        upsertWorkspaceChatMessage(state, action) {
            state.chatMessages = upsertByDocumentId(state.chatMessages, action.payload);
        },
        upsertWorkspaceChatParticipant(state, action) {
            state.chatParticipants = upsertByDocumentId(state.chatParticipants, action.payload);
        },
        upsertWorkspaceInboxItem(state, action) {
            state.inboxItems = upsertByDocumentId(state.inboxItems, action.payload);
        },
        upsertWorkspaceUserRole(state, action) {
            const role = action.payload;
            state.userRoles = upsertByDocumentId(state.userRoles, role);
            state.workspaceMembership = patchMembershipUserRole(state.workspaceMembership, role);
            state.workspaceMemberships = state.workspaceMemberships.map((membership) => (
                patchMembershipUserRole(membership, role)
            ));
        },
        removeWorkspaceTask(state, action) {
            state.tasks = removeByDocumentId(state.tasks, action.payload);
        },
        removeWorkspaceProject(state, action) {
            state.projects = removeByDocumentId(state.projects, action.payload);
        },
        removeWorkspaceGoal(state, action) {
            state.goals = removeByDocumentId(state.goals, action.payload);
        },
        removeWorkspaceTeam(state, action) {
            state.teams = removeByDocumentId(state.teams, action.payload);
        },
        removeWorkspaceCustomer(state, action) {
            state.customers = removeByDocumentId(state.customers, action.payload);
        },
        removeWorkspaceOrder(state, action) {
            state.orders = removeByDocumentId(state.orders, action.payload);
        },
        removeWorkspaceInvoice(state, action) {
            state.invoices = removeByDocumentId(state.invoices, action.payload);
        },
        removeWorkspacePayment(state, action) {
            state.payments = removeByDocumentId(state.payments, action.payload);
        },
        removeWorkspaceSubscription(state, action) {
            state.subscriptions = removeByDocumentId(state.subscriptions, action.payload);
        },
        removeWorkspaceBillingProfile(state, action) {
            state.billingProfiles = removeByDocumentId(state.billingProfiles, action.payload);
        },
        removeWorkspaceCreditWallet(state, action) {
            state.creditWallets = removeByDocumentId(state.creditWallets, action.payload);
        },
        removeWorkspaceCreditLedgerEntry(state, action) {
            state.creditLedgerEntries = removeByDocumentId(state.creditLedgerEntries, action.payload);
        },
        removeWorkspaceManualPaymentRequest(state, action) {
            state.manualPaymentRequests = removeByDocumentId(state.manualPaymentRequests, action.payload);
        },
        removeWorkspaceChat(state, action) {
            state.chats = removeByDocumentId(state.chats, action.payload);
        },
        removeWorkspaceChatMessage(state, action) {
            state.chatMessages = removeByDocumentId(state.chatMessages, action.payload);
        },
        removeWorkspaceChatParticipant(state, action) {
            state.chatParticipants = removeByDocumentId(state.chatParticipants, action.payload);
        },
        removeWorkspaceInboxItem(state, action) {
            state.inboxItems = removeByDocumentId(state.inboxItems, action.payload);
        },
        removeWorkspaceUserRole(state, action) {
            const role = action.payload;
            state.userRoles = removeByDocumentId(state.userRoles, role);
            state.workspaceMembership = patchMembershipUserRole(state.workspaceMembership, role, true);
            state.workspaceMemberships = state.workspaceMemberships.map((membership) => (
                patchMembershipUserRole(membership, role, true)
            ));
        },
        applyWorkspaceRealtimeEvent(state, action) {
            state.lastEvent = action.payload;
        },
    }
});

export const {
    hydrateWorkspaceSnapshot,
    setWorkspaceGraphWorkspaceId,
    setWorkspaceGraphMembership,
    setWorkspaceGraphMemberships,
    resetWorkspaceGraph,
    setWorkspaceGraphTasks,
    mergeWorkspaceTasks,
    setWorkspaceGraphProjects,
    setWorkspaceGraphGoals,
    setWorkspaceGraphTeams,
    setWorkspaceGraphCustomers,
    setWorkspaceGraphSubscriptionPlans,
    setWorkspaceGraphOrders,
    setWorkspaceGraphInvoices,
    setWorkspaceGraphPayments,
    setWorkspaceGraphSubscriptions,
    setWorkspaceGraphBillingProfiles,
    setWorkspaceGraphCreditWallets,
    setWorkspaceGraphCreditLedgerEntries,
    setWorkspaceGraphManualPaymentRequests,
    setWorkspaceGraphChats,
    mergeWorkspaceChats,
    setWorkspaceGraphChatMessages,
    mergeWorkspaceChatMessages,
    replaceWorkspaceChatMessagesForChat,
    setWorkspaceGraphChatParticipants,
    mergeWorkspaceChatParticipants,
    setWorkspaceGraphInboxItems,
    mergeWorkspaceInboxItems,
    upsertWorkspaceTask,
    upsertWorkspaceProject,
    upsertWorkspaceGoal,
    upsertWorkspaceTeam,
    upsertWorkspaceCustomer,
    upsertWorkspaceOrder,
    upsertWorkspaceInvoice,
    upsertWorkspacePayment,
    upsertWorkspaceSubscription,
    upsertWorkspaceBillingProfile,
    upsertWorkspaceCreditWallet,
    upsertWorkspaceCreditLedgerEntry,
    upsertWorkspaceManualPaymentRequest,
    upsertWorkspaceChat,
    upsertWorkspaceChatMessage,
    upsertWorkspaceChatParticipant,
    upsertWorkspaceInboxItem,
    upsertWorkspaceUserRole,
    removeWorkspaceTask,
    removeWorkspaceProject,
    removeWorkspaceGoal,
    removeWorkspaceTeam,
    removeWorkspaceCustomer,
    removeWorkspaceOrder,
    removeWorkspaceInvoice,
    removeWorkspacePayment,
    removeWorkspaceSubscription,
    removeWorkspaceBillingProfile,
    removeWorkspaceCreditWallet,
    removeWorkspaceCreditLedgerEntry,
    removeWorkspaceManualPaymentRequest,
    removeWorkspaceChat,
    removeWorkspaceChatMessage,
    removeWorkspaceChatParticipant,
    removeWorkspaceInboxItem,
    removeWorkspaceUserRole,
    applyWorkspaceRealtimeEvent,
} = workspaceGraphSlice.actions;

export const workspaceGraphSelectors = {
    tasks: (state) => state.workspaceGraph.tasks,
    projects: (state) => state.workspaceGraph.projects,
    goals: (state) => state.workspaceGraph.goals,
    teams: (state) => state.workspaceGraph.teams,
    customers: (state) => state.workspaceGraph.customers,
    subscriptionPlans: (state) => state.workspaceGraph.subscriptionPlans,
    orders: (state) => state.workspaceGraph.orders,
    invoices: (state) => state.workspaceGraph.invoices,
    payments: (state) => state.workspaceGraph.payments,
    subscriptions: (state) => state.workspaceGraph.subscriptions,
    billingProfiles: (state) => state.workspaceGraph.billingProfiles,
    creditWallets: (state) => state.workspaceGraph.creditWallets,
    creditLedgerEntries: (state) => state.workspaceGraph.creditLedgerEntries,
    manualPaymentRequests: (state) => state.workspaceGraph.manualPaymentRequests,
    chats: (state) => state.workspaceGraph.chats,
    chatMessages: (state) => state.workspaceGraph.chatMessages,
    chatParticipants: (state) => state.workspaceGraph.chatParticipants,
    inboxItems: (state) => state.workspaceGraph.inboxItems,
    workspaceMembership: (state) => state.workspaceGraph.workspaceMembership,
    workspaceMemberships: (state) => state.workspaceGraph.workspaceMemberships,
    userRoles: (state) => state.workspaceGraph.userRoles,
    workspaceId: (state) => state.workspaceGraph.workspaceId,
};

export { collectWorkspaceTasks };

export default workspaceGraphSlice.reducer;
